import { Box, Alert, Typography, IconButton } from "@mui/material";
import { useTranslationApp } from "../../lib/i18next";
import {
  ADS_CAMPAIGN_ENTITIES,
  ADS_OBJECT_TYPES,
  ALERTS_CAMPAIGNS_TEXT,
  BORDER_RADIUS,
  YELLOW_ALERT_TYPES,
  ALERT_COLORS,
} from "../../utils/constants";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { getAlertMessage } from "../../utils/campaignAlerts";

const AlertBanner = ({
  platformsData,
  sectionSelected = {},
  levelSectionSelected = "",
  currentLevel,
  onRemoveAlert,
}) => {
  const { t } = useTranslationApp();

  const currentPlatform = sectionSelected.platform;

  // Map currentLevel to entity (only platform needs mapping)
  const getEntityFromLevel = (level) => {
    if (level === "platform") {
      return "campaign";
    }
    return level;
  };

  // Get alerts only for the currently selected platform and level
  const getCurrentPlatformAlerts = () => {
    const allAlerts = [];
    const targetEntity = getEntityFromLevel(currentLevel);

    // Process the currently selected platform
    if (currentPlatform && platformsData[currentPlatform]) {
      const platformData = platformsData[currentPlatform];
      if (platformData?.alerts && Array.isArray(platformData.alerts)) {
        platformData.alerts.forEach((alert, originalIndex) => {
          if (!alert.entity === targetEntity) return;

          const isAlertCampaign =
            alert.entity === ADS_OBJECT_TYPES.campaign &&
            levelSectionSelected === ADS_CAMPAIGN_ENTITIES.PLATFORM;
          const isAlertAdGroup =
            alert.entity === ADS_OBJECT_TYPES.adGroup &&
            alert.adGroupID === sectionSelected.adGroupID &&
            levelSectionSelected === ADS_CAMPAIGN_ENTITIES.AD_GROUP;
          const isAlertAd =
            alert.entity === ADS_OBJECT_TYPES.ad &&
            alert.adGroupID === sectionSelected.adGroupID &&
            alert.adID === sectionSelected.adID &&
            levelSectionSelected === ADS_CAMPAIGN_ENTITIES.AD;
          const isAlertAssetsGroup =
            alert.entity === ADS_OBJECT_TYPES.assetsGroup &&
            alert.assetsGroupID === sectionSelected.assetsGroupID &&
            levelSectionSelected === ADS_CAMPAIGN_ENTITIES.ASSETS_GROUP;

          if (
            isAlertCampaign ||
            isAlertAdGroup ||
            isAlertAd ||
            isAlertAssetsGroup
          ) {
            allAlerts.push({
              platform: currentPlatform,
              alertType: alert.type,
              message: getAlertMessage(alert),
              originalIndex,
            });
          }
        });
      }
    }

    return allAlerts.filter((a) => {
      if (ALERTS_CAMPAIGNS_TEXT?.[a.type]) {
        return true;
      }
      return a.message;
    });
  };

  const alerts = getCurrentPlatformAlerts();

  const getAlertColors = (alertType) => {
    const isYellow = YELLOW_ALERT_TYPES.includes(alertType);
    const theme = isYellow ? ALERT_COLORS.yellow : ALERT_COLORS.error;
    return {
      bg: theme.background,
      color: theme.color,
      border: theme.border,
    };
  };

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        mb: 1.5,
      }}
    >
      {alerts.map((alert) => {
        const colors = getAlertColors(alert.alertType);
        return (
          <Alert
            key={`${alert.platform}-${alert.alertType}-${alert.originalIndex}`}
            icon={false}
            sx={{
              width: "100%",
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
              borderRadius: BORDER_RADIUS,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              ".MuiAlert-action": {
                alignItems: "center",
                p: 0,
              },
            }}
            action={
              onRemoveAlert && (
                <IconButton
                  size="small"
                  onClick={() =>
                    onRemoveAlert(
                      alert.platform,
                      alert.alertType,
                      alert.originalIndex
                    )
                  }
                  sx={{
                    color: `${colors.color} !important`,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <CloseIcon
                    fontSize="small"
                    sx={{ color: `${colors.color} !important` }}
                  />
                </IconButton>
              )
            }
          >
            <Typography
              variant="body2"
              sx={{
                width: "100%",
                color: `${colors.color} !important`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ErrorOutlineOutlinedIcon
                sx={{ color: `${colors.color} !important`, fontSize: "1.3rem" }}
              />
              {t(alert.message, { platform: t(alert.platform) })}
            </Typography>
          </Alert>
        );
      })}
    </Box>
  );
};

export default AlertBanner;
